import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import Login from '../Login/index';

const Profile = () => {
  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts.profile : enTexts.profile;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    fullname: '',
    email: currentUser?.email || '',
    phone: '',
    universityName: currentUser?.universityName || '',
    isAdmin: Boolean(currentUser?.isAdmin),
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const backTarget = '/admin';
  const backLabel = texts.back_to_admin;

  const getRawToken = () => {
    return (
      localStorage.getItem('userToken') || (typeof token === 'string' ? token : token?.token || '')
    );
  };

  const fetchProfile = async () => {
    const rawToken = getRawToken();
    if (!rawToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/users/profile`,
        {
          headers: { Authorization: `Bearer ${rawToken}` },
        }
      );
      const user = response.data.user;
      if (user) {
        setProfile({
          fullname: user.fullname || '',
          email: user.email || currentUser?.email || '',
          phone: user.phone || '',
          universityName: user.university?.name || currentUser?.universityName || '-',
          isAdmin: Boolean(user.is_admin),
        });
      }
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      if (currentUser?.email) {
        setProfile(prev => ({
          ...prev,
          email: prev.email || currentUser.email,
          isAdmin: prev.isAdmin || Boolean(currentUser.isAdmin),
          universityName: prev.universityName || currentUser.universityName || '-',
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [token, lang]);

  useEffect(() => {
    if (currentUser?.email) {
      setProfile(prev => ({
        ...prev,
        email: prev.email || currentUser.email,
        isAdmin: prev.isAdmin || Boolean(currentUser.isAdmin),
        universityName: prev.universityName || currentUser.universityName || '-',
      }));
    }
  }, [currentUser]);

  if (!token) {
    return <Login />;
  }

  const handleSubmit = async e => {
    e.preventDefault();
    setFeedback({ type: '', message: '' });

    if (newPassword || confirmPassword) {
      if (newPassword.length < 6) {
        setFeedback({ type: 'danger', message: texts.password_min_length });
        return;
      }
      if (newPassword !== confirmPassword) {
        setFeedback({ type: 'danger', message: texts.password_mismatch });
        return;
      }
    }

    setSaving(true);
    try {
      const rawToken = getRawToken();
      const payload = {
        fullname: profile.fullname,
        phone: profile.phone,
      };
      if (newPassword && newPassword.trim() !== '') {
        payload.password = newPassword;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/users/profile`,
        payload,
        {
          headers: { Authorization: `Bearer ${rawToken}` },
        }
      );

      if (response.data.user) {
        setProfile(prev => ({
          ...prev,
          fullname: response.data.user.fullname || '',
          phone: response.data.user.phone || '',
        }));
      }

      setFeedback({ type: 'success', message: texts.success });
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 4000);
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      setFeedback({
        type: 'danger',
        message: error.response?.data?.message || texts.error,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4 py-lg-5" style={{ minHeight: '80vh' }}>
      {/* Top Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-3 border-bottom">
        <div>
          <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
            <i className="fa fa-user-circle"></i>
            <span>{texts.title}</span>
          </h2>
          <p className="text-muted mb-0 small">
            {lang === 'es'
              ? 'Gestiona tu información de contacto y credenciales de acceso a la plataforma.'
              : 'Manage your contact details and account login credentials.'}
          </p>
        </div>
        <div className="mt-3 mt-sm-0">
          <Link
            to={backTarget}
            className="btn btn-outline-secondary px-3 py-2 rounded-3 d-flex align-items-center gap-2 shadow-sm"
          >
            <i className="fa fa-arrow-left"></i>
            <span>{backLabel}</span>
          </Link>
        </div>
      </div>

      {feedback.message && (
        <div
          className={`alert alert-${feedback.type} alert-dismissible fade show rounded-3 shadow-sm mb-4`}
          role="alert"
        >
          <i
            className={`fa ${feedback.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}
          ></i>
          <strong>{feedback.message}</strong>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div
            className="spinner-border text-primary"
            style={{ width: '3rem', height: '3rem' }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="d-flex flex-column gap-4">
            {/* Card 1: Account & Contact Information */}
            <div className="card border-0 shadow-sm rounded-4 bg-white">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-3 bg-sen text-white shadow-sm"
                      style={{ width: '42px', height: '42px', fontSize: '18px' }}
                    >
                      <i className="fa fa-address-card-o"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold mb-0 text-dark">{texts.contact_info}</h5>
                      <span className="text-muted small">
                        {lang === 'es'
                          ? 'Datos personales y de identificación en el sistema'
                          : 'Personal details and account identification in the system'}
                      </span>
                    </div>
                  </div>

                  {/* Role and University Badges */}
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span
                      className={`badge ${profile.isAdmin ? 'bg-primary' : 'bg-secondary'} px-3 py-2 rounded-pill`}
                    >
                      <i className={`fa ${profile.isAdmin ? 'fa-shield' : 'fa-id-badge'} me-1`}></i>
                      {profile.isAdmin ? texts.admin_role : texts.user_role}
                    </span>
                    {profile.universityName &&
                    profile.universityName !== '-' &&
                    profile.universityName !== '' ? (
                      <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                        <i className="fa fa-university me-1 text-primary"></i>
                        {profile.universityName}
                      </span>
                    ) : (
                      <span className="badge bg-light text-secondary border px-3 py-2 rounded-pill">
                        <i className="fa fa-university me-1"></i>
                        {texts.no_university}
                      </span>
                    )}
                  </div>
                </div>

                <div className="row g-4">
                  {/* Email (Account Identifier - Read-only) */}
                  <div className="col-12 col-md-4">
                    <label className="form-label small fw-bold text-secondary d-flex align-items-center justify-content-between">
                      <span>{texts.email}</span>
                      <small className="text-muted fw-normal">
                        <i className="fa fa-lock me-1"></i>
                        {lang === 'es' ? 'Cuenta' : 'Account'}
                      </small>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 text-muted">
                        <i className="fa fa-envelope"></i>
                      </span>
                      <input
                        type="email"
                        className="form-control bg-light border-start-0 py-2 text-muted"
                        value={profile.email}
                        disabled
                        readOnly
                        style={{ cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>

                  {/* Full Name */}
                  <div className="col-12 col-md-4">
                    <label className="form-label small fw-bold text-secondary">
                      {texts.fullname}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 text-muted">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control bg-light border-start-0 py-2"
                        value={profile.fullname}
                        onChange={e => setProfile({ ...profile, fullname: e.target.value })}
                        placeholder={lang === 'es' ? 'Ej: Juan Pérez' : 'e.g. John Doe'}
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="col-12 col-md-4">
                    <label className="form-label small fw-bold text-secondary">{texts.phone}</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 text-muted">
                        <i className="fa fa-phone"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control bg-light border-start-0 py-2"
                        value={profile.phone}
                        onChange={e => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="+56 9 1234 5678"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Password Security */}
            <div className="card border-0 shadow-sm rounded-4 bg-white">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 bg-sen text-white shadow-sm"
                    style={{ width: '42px', height: '42px', fontSize: '18px' }}
                  >
                    <i className="fa fa-lock"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-0 text-dark">{texts.change_password}</h5>
                    <span className="text-muted small">
                      {lang === 'es'
                        ? 'Ingresa una nueva contraseña si deseas actualizar tu clave por defecto (mínimo 6 caracteres).'
                        : 'Enter a new password if you wish to change your default password (minimum 6 characters).'}
                    </span>
                  </div>
                </div>

                <div className="row g-4 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-bold text-secondary">
                      {texts.new_password}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 text-muted">
                        <i className="fa fa-key"></i>
                      </span>
                      <input
                        type="password"
                        className="form-control bg-light border-start-0 py-2"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder={texts.new_password_placeholder}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-bold text-secondary">
                      {texts.confirm_password}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 text-muted">
                        <i className="fa fa-shield"></i>
                      </span>
                      <input
                        type="password"
                        className="form-control bg-light border-start-0 py-2"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder={texts.confirm_password_placeholder}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Actions Toolbar */}
            <div className="d-flex justify-content-end align-items-center gap-3 pt-2">
              <button
                type="button"
                className="btn btn-light px-4 py-2 rounded-3 fw-semibold border"
                onClick={() => navigate(backTarget)}
                disabled={saving}
              >
                {texts.close}
              </button>
              <button
                type="submit"
                className="btn btn-primary bg-sen border-0 px-5 py-2 rounded-3 fw-bold shadow-sm"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {texts.saving}
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-2"></i>
                    {texts.save}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Profile;
